/**
 * @file components/chat/ConversationList.tsx
 * 7.3-06 — Liste de conversations locale (style WhatsApp).
 *
 * Créer / renommer / supprimer un fil, aperçu du dernier message + heure,
 * pastille de non-lus. Tout passe par l'abstraction ChatStore (threadStorage),
 * prête à être branchée sur l'API /channels du backend (P1) sans refonte UI.
 *
 * Responsive : colonne fixe en desktop, drawer coulissant en mobile (géré par
 * le parent via les props `open`/`onClose`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, MessageSquarePlus, Pencil, Trash2, X } from 'lucide-react';
import { chatStore } from '../../utils/threadStorage.ts';
import type { ConversationMeta } from '../../utils/threadStorage.ts';

// ── Hook de gestion de la liste ──────────────────────────────────────────────

export interface Conversations {
  conversations: ConversationMeta[];
  activeId: string | null;
  loading: boolean;
  select: (id: string) => void;
  create: () => void;
  rename: (id: string, title: string) => void;
  remove: (id: string) => void;
  /** Rafraîchit les métadonnées (aperçus, horodatages). */
  refresh: () => void;
}

/**
 * État de la liste de conversations, adossé au ChatStore.
 * @param beforeSwitch sauvegarde immédiate du fil courant avant bascule
 *        (évite de perdre la fenêtre de debounce de useChatThread).
 */
export function useConversations(beforeSwitch?: () => Promise<void>): Conversations {
  const [conversations, setConversations] = useState<ConversationMeta[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const beforeSwitchRef = useRef(beforeSwitch);
  beforeSwitchRef.current = beforeSwitch;

  const refresh = useCallback(() => {
    void chatStore.listConversations().then(setConversations);
  }, []);

  useEffect(() => {
    let cancelled = false;
    chatStore
      .getActiveConversationId()
      .then(async (id) => {
        if (cancelled) return;
        setActiveId(id);
        setConversations(await chatStore.listConversations());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const select = useCallback(
    (id: string) => {
      void (async () => {
        await beforeSwitchRef.current?.();
        await chatStore.setActiveConversation(id);
        setActiveId(id);
        refresh();
      })();
    },
    [refresh],
  );

  const create = useCallback(() => {
    void (async () => {
      await beforeSwitchRef.current?.();
      const meta = await chatStore.createConversation();
      setActiveId(meta.id);
      refresh();
    })();
  }, [refresh]);

  const rename = useCallback(
    (id: string, title: string) => {
      void chatStore.renameConversation(id, title).then(refresh);
    },
    [refresh],
  );

  const remove = useCallback(
    (id: string) => {
      void (async () => {
        await chatStore.deleteConversation(id);
        const nextActive = await chatStore.getActiveConversationId();
        setActiveId(nextActive);
        refresh();
      })();
    },
    [refresh],
  );

  return { conversations, activeId, loading, select, create, rename, remove, refresh };
}

// ── Présentation ─────────────────────────────────────────────────────────────

function formatListTime(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return sameDay
    ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function ConversationItem({
  conv,
  active,
  onSelect,
  onRename,
  onDelete,
}: {
  conv: ConversationMeta;
  active: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(conv.title);

  const commitRename = () => {
    setEditing(false);
    const title = draft.trim();
    if (title && title !== conv.title) onRename(title);
    else setDraft(conv.title);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-border bg-accent/40 px-2 py-2">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') {
              setDraft(conv.title);
              setEditing(false);
            }
          }}
          aria-label="Nouveau nom de la conversation"
          className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          type="button"
          aria-label="Valider le nom"
          onClick={commitRename}
          className="rounded p-1 text-green-600 hover:bg-accent"
        >
          <Check size={14} />
        </button>
        <button
          type="button"
          aria-label="Annuler le renommage"
          onClick={() => {
            setDraft(conv.title);
            setEditing(false);
          }}
          className="rounded p-1 text-muted-foreground hover:bg-accent"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group relative rounded-lg transition-colors ${
        active ? 'bg-primary/10' : 'hover:bg-accent'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="w-full px-3 py-2 text-left focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
      >
        <span className="flex items-baseline justify-between gap-2">
          <span
            className={`truncate text-sm ${
              active ? 'font-semibold text-foreground' : 'font-medium text-foreground'
            }`}
          >
            {conv.title}
          </span>
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {formatListTime(conv.updatedAt)}
          </span>
        </span>
        <span className="mt-0.5 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-muted-foreground">{conv.lastPreview}</span>
          {conv.unread > 0 && (
            <span
              aria-label={`${conv.unread} messages non lus`}
              className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground"
            >
              {conv.unread}
            </span>
          )}
        </span>
      </button>

      {/* Actions (renommer / supprimer) — visibles au survol ou au clavier */}
      <div className="absolute right-2 top-1.5 flex gap-0.5 rounded-md bg-card/90 opacity-0 shadow-sm transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <button
          type="button"
          aria-label={`Renommer la conversation ${conv.title}`}
          title="Renommer"
          onClick={() => {
            setDraft(conv.title);
            setEditing(true);
          }}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <Pencil size={13} />
        </button>
        <button
          type="button"
          aria-label={`Supprimer la conversation ${conv.title}`}
          title="Supprimer"
          onClick={() => {
            // Confirmation simple — la suppression est définitive (stockage local).
            if (window.confirm(`Supprimer la conversation « ${conv.title} » ?`)) onDelete();
          }}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-red-500"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

export default function ConversationList({
  conversations,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}: {
  conversations: ConversationMeta[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}): React.ReactElement {
  return (
    <div className="flex h-full w-full flex-col bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-sm font-semibold text-foreground">Conversations</span>
        <button
          type="button"
          aria-label="Nouvelle conversation"
          title="Nouvelle conversation"
          onClick={onCreate}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <MessageSquarePlus size={17} />
        </button>
      </div>
      <nav aria-label="Liste des conversations" className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {conversations.map((c) => (
          <ConversationItem
            key={c.id}
            conv={c}
            active={c.id === activeId}
            onSelect={() => onSelect(c.id)}
            onRename={(title) => onRename(c.id, title)}
            onDelete={() => onDelete(c.id)}
          />
        ))}
        {conversations.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            Aucune conversation. Créez-en une avec le bouton ci-dessus.
          </p>
        )}
      </nav>
    </div>
  );
}
